import React from 'react';
import { Tooltip } from './tooltip';

interface SwitchProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  id?: string;
  /**
   * Additive, and defaulted off so no existing call site changes behaviour.
   * A styled-but-live switch is worse than none: it reads as usable, fires
   * `onCheckedChange`, and the refusal arrives from somewhere else entirely.
   */
  disabled?: boolean;
  /** Why it is disabled. Rendered as the shared Tooltip, which opens on a disabled switch. */
  title?: string;
  /**
   * Accessible name. Additive and optional, so the four existing call sites are
   * unchanged — they render the label as adjacent text, which a sighted user
   * reads but a screen reader never associates with the control.
   */
  'aria-label'?: string;
}

export function Switch({
  checked = false,
  onCheckedChange,
  className = '',
  id,
  disabled = false,
  title,
  'aria-label': ariaLabel,
}: SwitchProps) {
  return (
    <Tooltip content={title}>
      <button
        id={id}
        role="switch"
        type="button"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          onCheckedChange?.(!checked);
        }}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </Tooltip>
  );
}
